import React, { useState } from 'react';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';

/**
 * チェックボックスオプションのインターフェース
 * @property {string} id - チェックボックスの一意な識別子
 * @property {string} label - チェックボックスに表示するラベルテキスト
 * @property {boolean} [defaultChecked] - デフォルトのチェック状態（任意）
 */
export interface CheckboxOption {
  id: string;
  label?: string;
  defaultChecked?: boolean;
  tooltip?: string;
}

/**
 * CheckboxGroupのプロパティインターフェース
 * @property {CheckboxOption[]} options - チェックボックスのオプション配列
 * @property {(state: Record<string, boolean>) => void} [onChange] - 状態変更時のコールバック（任意）
 * @property {string} [className] - コンテナに適用するCSSクラス（任意）
 * @property {string} [labelClassName] - ラベルに適用するCSSクラス（任意）
 */
interface CheckboxGroupProps {
  options: CheckboxOption[];
  onChange?: (state: Record<string, boolean>) => void;
  className?: string;
  labelClassName?: string;
}

/**
 * 汎用的なチェックボックスグループコンポーネント
 * @param {CheckboxGroupProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} チェックボックスグループ要素
 */
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  onChange,
  className = '',
  labelClassName = 'ml-2'
}) => {
  // 初期状態をoptionsから生成
  const [checkState, setCheckState] = useState<Record<string, boolean>>(
    options.reduce((acc, option) => {
      acc[option.id] = option.defaultChecked || false;
      return acc;
    }, {} as Record<string, boolean>)
  );

  /**
   * チェックボックスの状態変更を処理
   * @param {CheckboxChangeEvent} e - PrimeReactのチェックボックス変更イベント
   */
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const newState = {
      ...checkState,
      [e.target.name]: e.checked
    } as Record<string,boolean>

    setCheckState(newState);

    // コールバック関数が提供されている場合、新しい状態を渡す
    if (onChange) {
      onChange(newState);
    }
  };

  return (
    <div className={`flex flex-row gap-3 flex-wrap ${className}`}>
      {options.map((option) => (
        <div key={option.id} className="flex align-items-center">
          <Checkbox
            inputId={option.id}
            name={option.id}
            checked={checkState[option.id]}
            onChange={handleCheckboxChange}
            tooltip={option.tooltip}
            tooltipOptions={{ position: 'top' }}
          />
          <label htmlFor={option.id} className={`${labelClassName} text-nowrap`}>
            {option.label ?? option.id}
          </label>
        </div>
      ))}
    </div>
  );
};

export default CheckboxGroup;
